/**
 * TanStack Query hooks for the Cloud (Azure) board.
 *
 * Every endpoint is backed by a server-side cache the backend poller refreshes
 * on fixed cadence tiers (fast 5 min, medium 15 min, slow/cost 6 h) — the
 * browser never holds an Azure credential and never calls ARM. The refetch
 * intervals here are therefore *aligned to those tiers*: asking more often
 * than the backend refreshes just re-serves the same snapshot, and asking
 * much less often leaves a long-lived tab stale. For the 6-hour tiers we
 * refetch every 30 minutes — that bounds tab staleness without pretending the
 * data moves faster than it does.
 *
 * None of these endpoints fail when Azure is unconfigured or a subscription
 * refuses a call: they answer 200 with `configured` / `status_detail` set (and
 * `errors` populated), and the UI explains the gap. A thrown error here
 * therefore means the *NOVA* API is down, not Azure.
 */

import { useQuery } from "@tanstack/react-query";

import {
  getCloudAdvisor,
  getCloudAlerts,
  getCloudChanges,
  getCloudCost,
  getCloudGovernance,
  getCloudOverview,
  getCloudServiceHealth,
  getCloudWorkloads,
} from "@/lib/api/generated";
import { logger } from "@/lib/logger";

/** Shared prefix, so a future invalidation can drop the whole board at once. */
const CLOUD_KEY = ["cloud"] as const;

/** Fast tier: firing alerts + resource health rebuild every 5 min. */
const FAST_REFETCH_MS = 5 * 60_000;

/** Medium tier: estate, changes, and service health rebuild every 15 min. */
const MEDIUM_REFETCH_MS = 15 * 60_000;

/**
 * Slow/cost tiers (advisor, governance, cost) rebuild every 6 h; 30 min keeps
 * an open tab within half an hour of a rebuild without polling theatre.
 */
const SLOW_REFETCH_MS = 30 * 60_000;

export function useCloudOverview() {
  return useQuery({
    queryKey: [...CLOUD_KEY, "overview"],
    queryFn: async () => {
      const { data } = await getCloudOverview();
      logger.debug("[cloud] overview received", {
        configured: data?.configured,
        reachable: data?.summary.subscriptions_reachable,
        expected: data?.summary.subscriptions_expected,
        firing: data?.summary.firing_alert_count,
        errors: data?.errors.length,
      });
      return data;
    },
    // The estate itself is medium-tier, but the summary carries fast-tier
    // enrichment (firing alerts, 24 h changes) — follow the faster cadence.
    refetchInterval: FAST_REFETCH_MS,
  });
}

export function useCloudWorkloads() {
  return useQuery({
    queryKey: [...CLOUD_KEY, "workloads"],
    queryFn: async () => {
      const { data } = await getCloudWorkloads();
      logger.debug("[cloud] workloads received", {
        total: data?.summary.total,
        stopped: data?.summary.stopped,
      });
      return data;
    },
    refetchInterval: MEDIUM_REFETCH_MS,
  });
}

export function useCloudServiceHealth() {
  return useQuery({
    queryKey: [...CLOUD_KEY, "service-health"],
    queryFn: async () => {
      const { data } = await getCloudServiceHealth();
      logger.debug("[cloud] service health received", {
        activeIssues: data?.active_issues,
        checkedAt: data?.checked_at,
      });
      return data;
    },
    refetchInterval: MEDIUM_REFETCH_MS,
  });
}

export function useCloudAdvisor() {
  return useQuery({
    queryKey: [...CLOUD_KEY, "advisor"],
    queryFn: async () => {
      const { data } = await getCloudAdvisor();
      logger.debug("[cloud] advisor received", { total: data?.total, high: data?.high });
      return data;
    },
    refetchInterval: SLOW_REFETCH_MS,
  });
}

export function useCloudAlerts() {
  return useQuery({
    queryKey: [...CLOUD_KEY, "alerts"],
    queryFn: async () => {
      const { data } = await getCloudAlerts();
      logger.debug("[cloud] alerts received", {
        total: data?.total,
        active: data?.active,
        truncated: data?.truncated,
      });
      return data;
    },
    refetchInterval: FAST_REFETCH_MS,
  });
}

export function useCloudChanges() {
  return useQuery({
    queryKey: [...CLOUD_KEY, "changes"],
    queryFn: async () => {
      const { data } = await getCloudChanges();
      logger.debug("[cloud] changes received", {
        total: data?.total,
        last24h: data?.total_last_24h,
      });
      return data;
    },
    refetchInterval: MEDIUM_REFETCH_MS,
  });
}

export function useCloudCost() {
  return useQuery({
    queryKey: [...CLOUD_KEY, "cost"],
    queryFn: async () => {
      const { data } = await getCloudCost();
      logger.debug("[cloud] cost received", {
        latestDay: data?.latest_day,
        monthToDate: data?.month_to_date,
        subscriptions: data?.subscriptions.length,
      });
      return data;
    },
    refetchInterval: SLOW_REFETCH_MS,
  });
}

export function useCloudGovernance() {
  return useQuery({
    queryKey: [...CLOUD_KEY, "governance"],
    queryFn: async () => {
      const { data } = await getCloudGovernance();
      logger.debug("[cloud] governance received", {
        nonCompliant: data?.non_compliant,
        baselineDay: data?.delta_baseline_day,
      });
      return data;
    },
    refetchInterval: SLOW_REFETCH_MS,
  });
}
